import { type ApiClient, getResponseData } from "@/modules/api/apiClient.ts";
import { canCreateResourceLocationsAtom } from "@/modules/api/locations.store.ts";
import { atomAllLocationsQuery } from "@/modules/location/atomAllLocationsQuery.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { type QueryKey, queryOptions } from "@tanstack/react-query";
import type { HostPool } from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries, mergeMaps } from "@warrenio/utils/collections/maps";
import { atomFamily } from "jotai-family";

interface Params {
    location: string;
}

export type HostPoolUUID = string;

export interface HostPoolWithType extends HostPool, Params {
    $type: "host_pool";
}

type Response = Map<HostPool["uuid"], HostPoolWithType>;

function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["user-resource/host_pool/list"] : ["user-resource/host_pool/list", params.location];
}

function hostPoolFromData(data: HostPool, location: string): HostPoolWithType {
    return { ...data, $type: "host_pool", location };
}

function getSingleQuery(apiClient: ApiClient, params: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }): Promise<Response> => {
            const { location } = params;
            const response = getResponseData(
                await apiClient.GET("/{location}/user-resource/host_pool/list", {
                    signal,
                    params: { path: { location } },
                }),
            );
            return mapFromEntries(response, (pool: HostPool) => [pool.uuid, hostPoolFromData(pool, location)]);
        },
    });
}

export const hostPoolQueryAtom = atomFamily((location: string) =>
    atomFromStandardQueryOptions(getSingleQuery, { location }),
);

/** Only load host pools for locations that you can create resources in (to prevent offline/broken locations breaking VM create) */
export const canCreateResourceHostPoolsAtom = atomAllLocationsQuery(
    hostPoolQueryAtom,
    (results) => [...mergeMaps(results).values()],
    canCreateResourceLocationsAtom,
);
